/**
 * Fund Detail Screen Component
 *
 * Main screen for viewing detailed information about a mutual fund.
 * Features:
 * - Header with fund name, rating badge, and actions (Copy ISIN, Watchlist)
 * - Score breakdown bars (consistency/recency/volatility)
 * - Performance returns over different periods
 * - Rating history timeline
 * - Fund info grid
 */

import { useState, useMemo } from "react";
import type { FundDetailData } from "@/hooks";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ArrowLeft, RefreshCw, AlertCircle } from "lucide-react";
import type { Fund, FundIsin, RankHistoryEntry } from "@curie/shared";

// Import the fund detail hook
import { useFundDetail, getFundDetailErrorMessage } from "@/hooks";

// Import components
import {
  FundDetailHeader,
  FundDetailHeaderSkeleton,
  type FundDetailHeaderData,
} from "./FundDetailHeader";
import {
  PerformanceReturnsSection,
  PerformanceReturnsSectionSkeleton,
  type PeriodReturnData,
} from "./PerformanceReturnsSection";
import {
  FundInfoSection,
  FundInfoSectionSkeleton,
  type FundInfoSectionData,
} from "./FundInfoSection";
import {
  ScoreBreakdownBars,
  ScoreBreakdownBarsSkeleton,
} from "@/components/holding-detail/ScoreBreakdownBars";
import {
  RatingHistoryChart,
  RatingHistoryChartSkeleton,
} from "@/components/holding-detail/RatingHistoryChart";
import {
  RatingHistoryList,
  RatingHistoryListSkeleton,
} from "@/components/holding-detail/RatingHistoryList";

// ============================================================================
// Types
// ============================================================================

/**
 * Props for the FundDetailScreen component
 */
export interface FundDetailScreenProps {
  /** The fund ISIN to display */
  fundId: FundIsin | string;
  /** Callback when back button is pressed */
  onBack?: () => void;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Types
// ============================================================================

/**
 * Consolidated view model for fund detail screen
 */
interface FundDetailViewModel {
  header: FundDetailHeaderData;
  performance: PeriodReturnData[];
  info: FundInfoSectionData;
  rankHistory: RankHistoryEntry[];
  currentRating: Fund["rating"];
  scores: {
    consistency: number;
    recency: number;
    volatility: number;
  };
}

// ============================================================================
// Data Transformation
// ============================================================================

/**
 * Transform fund data to view model in a single pass
 * Consolidates multiple transformations to avoid repeated object creation
 */
function transformToViewModel(data: FundDetailData): FundDetailViewModel {
  const { fund, rankHistory } = data;
  const history = rankHistory ?? [];
  const latestRating = history.length > 0 ? history[history.length - 1]?.rating : undefined;

  return {
    header: {
      isin: fund.isin,
      name: fund.name,
      amcName: fund.amcName,
      rating: fund.rating,
      category: fund.category,
      planType: fund.planType,
      nav: fund.nav,
      navDate: fund.navDate,
      returns1D: fund.returns1D,
      rank: fund.rank,
      totalFundsInCategory: fund.totalFundsInCategory,
    },
    performance: [
      { period: "1M", fundReturn: fund.returns1M },
      { period: "3M", fundReturn: fund.returns3M },
      { period: "6M", fundReturn: fund.returns6M },
      { period: "1Y", fundReturn: fund.returns1Y },
      { period: "3Y", fundReturn: fund.returns3Y },
      { period: "5Y", fundReturn: fund.returns5Y },
    ],
    info: {
      aum: fund.aum,
      expenseRatio: fund.expenseRatio,
      exitLoad: fund.exitLoad,
      category: fund.category,
      planType: fund.planType,
      launchDate: fund.launchDate,
      benchmarkIndex: fund.benchmarkIndex,
      minSipAmount: fund.minSipAmount,
      lockInPeriod: fund.lockInPeriod,
    },
    rankHistory: history,
    currentRating: latestRating ?? fund.rating,
    scores: {
      consistency: fund.consistencyScore,
      recency: fund.recencyScore,
      volatility: fund.volatilityScore,
    },
  };
}

// ============================================================================
// Subcomponents
// ============================================================================

/**
 * Screen header with back button and title
 */
function ScreenHeader({
  fundName,
  onBack,
  onRefresh,
  isRefreshing,
}: {
  fundName?: string;
  onBack?: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        {onBack && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="h-9 w-9"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        {fundName && (
          <h1 className="text-lg font-semibold truncate max-w-[200px] sm:max-w-none">
            {fundName}
          </h1>
        )}
      </div>
      {onRefresh && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="h-9 w-9"
          aria-label="Refresh data"
        >
          <RefreshCw
            className={cn("h-4 w-4", isRefreshing && "animate-spin")}
          />
        </Button>
      )}
    </div>
  );
}

/**
 * Error state display
 */
function ErrorState({
  error,
  onRetry,
  onBack,
}: {
  error: unknown;
  onRetry: () => void;
  onBack?: () => void;
}) {
  const message = getFundDetailErrorMessage(error);
  const isNotFound = message.includes("not found");

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
        <AlertCircle className="h-8 w-8 text-destructive" />
      </div>
      <h2 className="text-lg font-semibold mb-2">
        {isNotFound ? "Fund Not Found" : "Error Loading Fund"}
      </h2>
      <p className="text-muted-foreground text-center mb-6 max-w-md">{message}</p>
      <div className="flex gap-3">
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            Go Back
          </Button>
        )}
        {!isNotFound && <Button onClick={onRetry}>Try Again</Button>}
      </div>
    </div>
  );
}

// ============================================================================
// Loading Skeleton
// ============================================================================

/**
 * Full screen skeleton loader
 */
export function FundDetailScreenSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("flex flex-col min-h-screen", className)}>
      {/* Header skeleton */}
      <div className="flex items-center justify-between mb-4 px-4 pt-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-md" />
          <Skeleton className="h-5 w-40" />
        </div>
        <Skeleton className="h-9 w-9 rounded-md" />
      </div>

      {/* Fund header skeleton */}
      <div className="px-4">
        <FundDetailHeaderSkeleton />
      </div>

      {/* Content skeletons */}
      <div className="px-4 py-4 space-y-4">
        <ScoreBreakdownBarsSkeleton />
        <PerformanceReturnsSectionSkeleton />
        <RatingHistoryChartSkeleton />
        <RatingHistoryListSkeleton />
        <FundInfoSectionSkeleton />
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Fund Detail Screen
 *
 * Displays comprehensive information about a single mutual fund including
 * header, score breakdown, performance, rating history, and fund info.
 *
 * @example
 * ```tsx
 * function FundPage({ params }: { params: { fundId: string } }) {
 *   const navigate = useNavigate();
 *
 *   return (
 *     <FundDetailScreen
 *       fundId={params.fundId}
 *       onBack={() => navigate(-1)}
 *     />
 *   );
 * }
 * ```
 */
export function FundDetailScreen({
  fundId,
  onBack,
  className,
}: FundDetailScreenProps) {
  // State for watchlist (would typically come from a global store)
  const [isWatchlisted, setIsWatchlisted] = useState(false);

  // Fetch fund data using the useFundDetail hook
  const {
    data: fundData,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useFundDetail(fundId);

  // Transform all data in a single pass
  const viewModel = useMemo(
    () => (fundData ? transformToViewModel(fundData) : null),
    [fundData]
  );

  // Handle watchlist toggle
  const handleWatchlistToggle = () => {
    setIsWatchlisted(!isWatchlisted);
    // TODO: Persist to backend/store
  };

  // Handle error state
  if (isError) {
    return (
      <div className={cn("flex flex-col min-h-screen", className)}>
        <ScreenHeader onBack={onBack} />
        <ErrorState error={error} onRetry={() => refetch()} onBack={onBack} />
      </div>
    );
  }

  // Handle loading state
  if (isLoading) {
    return <FundDetailScreenSkeleton className={className} />;
  }

  return (
    <div className={cn("flex flex-col min-h-screen bg-background", className)}>
      {/* Screen Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b px-4 py-2">
        <ScreenHeader
          fundName={viewModel?.header.name}
          onBack={onBack}
          onRefresh={() => refetch()}
          isRefreshing={isRefetching}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 px-4 py-4 space-y-4">
        {/* Fund Header */}
        {viewModel ? (
          <FundDetailHeader
            data={viewModel.header}
            isWatchlisted={isWatchlisted}
            onWatchlistToggle={handleWatchlistToggle}
          />
        ) : (
          <FundDetailHeaderSkeleton />
        )}

        {/* Score Breakdown */}
        {viewModel ? (
          <ScoreBreakdownBars
            consistencyScore={viewModel.scores.consistency}
            recencyScore={viewModel.scores.recency}
            volatilityScore={viewModel.scores.volatility}
          />
        ) : (
          <ScoreBreakdownBarsSkeleton />
        )}

        {/* Performance Returns */}
        {viewModel ? (
          <PerformanceReturnsSection
            returns={viewModel.performance}
            fundName={viewModel.header.name}
            benchmarkName={viewModel.info.benchmarkIndex}
          />
        ) : (
          <PerformanceReturnsSectionSkeleton />
        )}

        {/* Rating History Chart */}
        {viewModel ? (
          <RatingHistoryChart
            rankHistory={viewModel.rankHistory}
            currentRating={viewModel.currentRating}
            title="Rating History"
          />
        ) : (
          <RatingHistoryChartSkeleton />
        )}

        {/* Rating History List */}
        {viewModel ? (
          <RatingHistoryList
            history={viewModel.rankHistory}
            title="Rating Timeline"
            initialDisplayCount={3}
            maxDisplayCount={12}
          />
        ) : (
          <RatingHistoryListSkeleton />
        )}

        {/* Fund Info */}
        {viewModel ? (
          <FundInfoSection data={viewModel.info} />
        ) : (
          <FundInfoSectionSkeleton />
        )}
      </div>
    </div>
  );
}

export default FundDetailScreen;
